import { z } from 'zod';
import { capacityTonnesSchema, vehicleColorSchema } from './fleet.schemas';
import { defineListQuery, listFilter } from './list-query.schemas';

export const TRAILER_STATUSES = ['active', 'inactive'] as const;
export type TrailerStatus = (typeof TRAILER_STATUSES)[number];

/** Derived from open trips, never stored (gitaumoses4/chukua#319): `GET /trailers?availability=` narrows on it. */
export const TRAILER_AVAILABILITY_FILTERS = ['available', 'unavailable'] as const;
export type TrailerAvailabilityFilter = (typeof TRAILER_AVAILABILITY_FILTERS)[number];

export type TrailerUnavailableReason = 'inactive' | 'on_trip';

export interface TrailerAvailability {
  isAvailable: boolean;
  reason: TrailerUnavailableReason | null;
  /** The trip holding the trailer, set only when `reason` is `on_trip`. */
  tripId?: string;
}

/** `POST /trailers` and `PATCH /trailers/:id` — status changes go through their own `activate`/`deactivate` routes. */
export const createTrailerSchema = z.object({
  registration: z.string().trim().min(1).max(32),
  typeCode: z.string().trim().min(1).max(32),
  capacityTonnes: capacityTonnesSchema,
  /** Carries a genset that powers a reefer container. What the trailer is (skeletal, flatbed, its axles) is its type's. */
  hasGenset: z.boolean(),
  /** The colour it is painted; omit to keep it (white on a new trailer). */
  color: vehicleColorSchema.optional(),
});
export type CreateTrailerInput = z.infer<typeof createTrailerSchema>;

export const updateTrailerSchema = createTrailerSchema;
export type UpdateTrailerInput = z.infer<typeof updateTrailerSchema>;

/** `GET /trailers` — search matches registration. */
export const trailerListQuerySchema = defineListQuery({
  sortFields: ['registration', 'createdAt'],
  defaultSort: { sort: 'registration', order: 'asc' },
  filters: {
    status: listFilter(z.enum(TRAILER_STATUSES)),
    availability: listFilter(z.enum(TRAILER_AVAILABILITY_FILTERS)),
  },
});
export type TrailerListQuery = z.infer<typeof trailerListQuerySchema>;
